// src/pages/Manager/OneOnOne.jsx
// STATUS: active | UPDATED: 2026-09-23
// LOG:
//   2026-09-23  New. The first of Jazz's management artefacts that comes back
//               filled in rather than printed.
//
// THE ONE ON ONE.
//
// `/manager/` has displayed his one on one guide since August. Displaying it is
// what every competitor does: a PDF, a blog post, a checklist a DMS vendor
// gives away to collect an email. Searched the market on 2026-09-23 and found
// nothing that lets a manager actually complete one. See docs/LANDSCAPE.md.
//
// WHAT IS ABOVE THE FOLD IS THE ARGUMENT.
//
// Not the form. The list of promises this floor made and has not gone back to
// check. His guide ends the coaching move with "set a time to inspect it again"
// and that is the half every manager skips, because nothing anywhere tracks it.
// A manager who opens this and sees three overdue inspections has learned
// something they could not have learned from paper.
//
// The form is second, because it is the part they already know how to do.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, HStack, VStack, Text, Textarea, Input, Button, Select, Spinner, Alert, AlertIcon,
  Badge, Divider, Tooltip,
} from '@chakra-ui/react'
import { AlertTriangle, Check, CalendarClock } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { displayNameFor } from '../../lib/names'
import {
  REVIEW, MOVE, listFloor, listRecent, listDue, getFor, saveOneOnOne, markInspected,
  vagueIn, moveProgress, moveComplete, inspectionState, dayKey, addDays,
} from '../../lib/oneOnOne'

const EMPTY = {}

function Field({ label, prompt, value, onChange, rows = 2, warn }) {
  return (
    <Box py={3} borderTop="1px solid" borderColor="line">
      <Text fontWeight={600} fontSize="body" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="sm" color="inkMuted" mb={2} maxW="measure" lineHeight={1.5}>
        {prompt}
      </Text>
      <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} fontSize="body" />
      {warn && (
        <HStack mt={2} spacing={2} color="warn" align="flex-start">
          <Box pt="2px">
            <AlertTriangle size={14} aria-hidden="true" />
          </Box>
          <Text fontSize="sm" maxW="measure" lineHeight={1.5}>
            &ldquo;{warn}&rdquo; is the kind of feedback his guide rules out. Say what you watched them
            do instead. You can still save this.
          </Text>
        </HStack>
      )}
    </Box>
  )
}

export default function OneOnOne() {
  const { user, profile } = useAuth()
  const dealershipId = profile?.dealership_id || null

  const [floor, setFloor] = useState([])
  const [due, setDue] = useState([])
  const [recent, setRecent] = useState([])
  const [who, setWho] = useState('')
  const [heldOn, setHeldOn] = useState(() => dayKey())
  const [draft, setDraft] = useState(EMPTY)
  const [state, setState] = useState({ loading: true, saving: false, error: null, saved: false })

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    const [f, d, r] = await Promise.all([
      listFloor(dealershipId),
      listDue(dealershipId),
      listRecent(dealershipId),
    ])
    setFloor(f.data)
    setDue(d.data)
    setRecent(r.data)
    const err = f.error || d.error || r.error
    setState({ loading: false, saving: false, error: err ? err.message || String(err) : null, saved: false })
  }, [dealershipId])

  useEffect(() => {
    load()
  }, [load])

  // Opening a person and a date shows what is already recorded for it, rather
  // than a blank form that would overwrite it on save.
  useEffect(() => {
    let live = true
    if (!who) {
      setDraft(EMPTY)
      return () => {
        live = false
      }
    }
    getFor(who, heldOn).then(({ data }) => {
      if (!live) return
      setDraft(data || EMPTY)
    })
    return () => {
      live = false
    }
  }, [who, heldOn])

  const nameOf = useMemo(() => {
    const map = {}
    for (const p of floor) map[p.id] = displayNameFor(p) || 'Somebody'
    return map
  }, [floor])

  const set = (key) => (v) => {
    setDraft((d) => ({ ...d, [key]: v }))
    setState((s) => ({ ...s, saved: false }))
  }

  const progress = moveProgress(draft)
  const complete = moveComplete(draft)

  const save = async () => {
    setState((s) => ({ ...s, saving: true, error: null, saved: false }))
    const fields = {}
    for (const f of [...REVIEW, ...MOVE]) fields[f.key] = draft[f.key]
    fields.inspect_on = draft.inspect_on || null
    const { error } = await saveOneOnOne({
      dealershipId,
      managerId: user?.id,
      salespersonId: who,
      heldOn,
      fields,
    })
    if (error) {
      setState((s) => ({ ...s, saving: false, error }))
      return
    }
    await load()
    setState((s) => ({ ...s, saved: true }))
  }

  const inspect = async (row) => {
    const { error } = await markInspected(row.id)
    if (error) {
      setState((s) => ({ ...s, error }))
      return
    }
    load()
  }

  if (state.loading) {
    return (
      <VStack align="stretch" maxW="sheet" spacing={6}>
        <Spinner size="sm" />
      </VStack>
    )
  }

  return (
    <VStack align="stretch" spacing={10} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          One of his daily disciplines
        </Text>
        <Text textStyle="pageTitle">The one on one</Text>
        <Text fontSize="lg" color="inkMuted" mt={2} maxW="measure" lineHeight={1.55}>
          His guide, as a thing you fill in rather than a thing you read. The part that matters is at
          the bottom of it: describe what you saw, say what it cost, show the correction, make them
          do it back, and set a date to come and look again.
        </Text>
      </Box>

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {!dealershipId && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          Your account is not attached to a store, so there is no floor to run these with.
        </Alert>
      )}

      {/* FIRST, BECAUSE IT IS THE PART NOTHING ELSE TRACKS. */}
      <Box>
        <HStack align="baseline" justify="space-between" mb={2} flexWrap="wrap" gap={2}>
          <Text fontSize="lg" fontWeight={600}>
            Waiting on another look
          </Text>
          {due.length > 0 && (
            <Badge colorScheme={due.some((r) => inspectionState(r) === 'overdue') ? 'red' : 'gray'} fontFamily="mono" fontSize="xs">
              {due.length}
            </Badge>
          )}
        </HStack>
        {due.length === 0 ? (
          <Text fontSize="body" color="inkMuted" maxW="measure" lineHeight={1.6}>
            Nothing is waiting. This fills with the promises you made to come back and check, so it
            stays empty either because you have kept up or because nobody has set a date yet.
          </Text>
        ) : (
          <VStack align="stretch" spacing={0}>
            {due.map((row) => {
              const st = inspectionState(row)
              return (
                <Box key={row.id} py={3} borderTop="1px solid" borderColor="line">
                  <HStack justify="space-between" align="baseline" flexWrap="wrap" gap={2} mb={1}>
                    <HStack spacing={2}>
                      <CalendarClock size={14} aria-hidden="true" />
                      <Text fontWeight={600} fontSize="body">
                        {nameOf[row.salesperson_id] || 'Somebody on your floor'}
                      </Text>
                      <Text
                        fontSize="sm"
                        fontFamily="mono"
                        color={st === 'overdue' ? 'danger' : 'inkDim'}
                      >
                        {st === 'overdue' ? 'overdue since' : 'due'} {row.inspect_on}
                      </Text>
                    </HStack>
                    <Button size="xs" variant="outline" leftIcon={<Check size={13} />} onClick={() => inspect(row)}>
                      Looked at it
                    </Button>
                  </HStack>
                  {row.correction && (
                    <Text fontSize="sm" color="inkMuted" maxW="measure" lineHeight={1.55}>
                      You showed them: {row.correction}
                    </Text>
                  )}
                </Box>
              )
            })}
          </VStack>
        )}
      </Box>

      <Divider />

      <Box>
        <Text fontSize="lg" fontWeight={600} mb={3}>
          Record one
        </Text>

        <HStack spacing={3} mb={1} flexWrap="wrap" rowGap={2}>
          <Select
            id="oneonone-who"
            value={who}
            onChange={(e) => setWho(e.target.value)}
            maxW="18rem"
            placeholder={floor.length ? 'Who was it with' : 'Nobody on your floor yet'}
            isDisabled={floor.length === 0}
          >
            {floor.map((p) => (
              <option key={p.id} value={p.id}>
                {displayNameFor(p) || p.id}
              </option>
            ))}
          </Select>
          <Input
            id="oneonone-date"
            type="date"
            value={heldOn}
            max={dayKey()}
            onChange={(e) => setHeldOn(e.target.value)}
            maxW="11rem"
            fontFamily="mono"
          />
          <Text fontSize="sm" color="inkDim" fontFamily="mono">
            {state.saving ? 'saving' : state.saved ? 'saved' : ''}
          </Text>
        </HStack>

        {who && (
          <>
            <Box mt={5}>
              <Text textStyle="pageEyebrow" mb={2}>
                What you went through
              </Text>
              {REVIEW.map((f) => (
                <Field
                  key={f.key}
                  label={f.label}
                  prompt={f.prompt}
                  value={draft[f.key]}
                  onChange={set(f.key)}
                />
              ))}
            </Box>

            <Box mt={8}>
              <HStack align="baseline" justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                <Text textStyle="pageEyebrow">The coaching move</Text>
                <Tooltip label="His five parts. Four of them is a description of a problem with no correction attached.">
                  <Text fontSize="sm" fontFamily="mono" color={complete ? 'success' : 'inkDim'}>
                    {progress.done} of {progress.total}
                  </Text>
                </Tooltip>
              </HStack>
              {MOVE.map((f) => (
                <Field
                  key={f.key}
                  label={f.label}
                  prompt={f.prompt}
                  value={draft[f.key]}
                  onChange={set(f.key)}
                  warn={vagueIn(draft[f.key])}
                />
              ))}

              <Box py={3} borderTop="1px solid" borderColor="line">
                <Text fontWeight={600} fontSize="body" mb={0.5}>
                  When you are coming back to look
                </Text>
                <Text fontSize="sm" color="inkMuted" mb={2} maxW="measure" lineHeight={1.5}>
                  The last part of his move, and the one everybody drops. A date here puts this at the
                  top of the page until somebody checks.
                </Text>
                <HStack spacing={2} flexWrap="wrap" rowGap={2}>
                  <Input
                    id="oneonone-inspect"
                    type="date"
                    value={draft.inspect_on || ''}
                    min={heldOn}
                    onChange={(e) => set('inspect_on')(e.target.value)}
                    maxW="11rem"
                    fontFamily="mono"
                  />
                  <Button size="sm" variant="ghost" onClick={() => set('inspect_on')(addDays(heldOn, 1))}>
                    Tomorrow
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => set('inspect_on')(addDays(heldOn, 7))}>
                    Next week
                  </Button>
                </HStack>
              </Box>
            </Box>

            <HStack mt={5} spacing={3} align="center" flexWrap="wrap" rowGap={2}>
              <Button onClick={save} isLoading={state.saving} isDisabled={!dealershipId || !who}>
                Save
              </Button>
              {!complete && (
                <Text fontSize="sm" color="inkDim" maxW="measure" flex="1 1 18rem">
                  You can save a partial one. The five part move is what he asks for, and it is the
                  half of this that changes anybody&rsquo;s behaviour.
                </Text>
              )}
            </HStack>
          </>
        )}
      </Box>

      {recent.length > 0 && (
        <Box>
          <Text fontSize="lg" fontWeight={600} mb={2}>
            Recently
          </Text>
          <VStack align="stretch" spacing={0}>
            {recent.map((row) => (
              <HStack key={row.id} py={3} borderTop="1px solid" borderColor="line" justify="space-between" align="baseline" flexWrap="wrap" gap={2}>
                <HStack spacing={3}>
                  <Text fontSize="sm" fontFamily="mono" color="inkDim">
                    {row.held_on}
                  </Text>
                  <Text fontWeight={600} fontSize="body">
                    {nameOf[row.salesperson_id] || 'Somebody'}
                  </Text>
                </HStack>
                <HStack spacing={3}>
                  {moveComplete(row) ? (
                    <Text fontSize="sm" color="success" fontFamily="mono">
                      full move
                    </Text>
                  ) : (
                    <Text fontSize="sm" color="inkDim" fontFamily="mono">
                      {moveProgress(row).done} of {moveProgress(row).total}
                    </Text>
                  )}
                  {row.inspected_at && (
                    <Text fontSize="sm" color="inkDim" fontFamily="mono">
                      checked
                    </Text>
                  )}
                </HStack>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  )
}
